import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  IconButton,
  Text,
  Tooltip,
  Collapse,
  useColorModeValue,
  Button,
  Flex,
  Divider,
  Badge
} from '@chakra-ui/react';
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowsOutCardinal,
  ArrowsInCardinal,
  House,
  Eye,
  Cube,
  CornersOut,
  CornersIn,
  X,
  CaretUp,
  CaretDown,
  CaretLeft,
  CaretRight,
  ArrowClockwise,
  ArrowCounterClockwise,
  CircleNotch
} from '@phosphor-icons/react';
import * as THREE from 'three';

const ThreeDCameraControls = ({ 
  camera,
  controls,
  model,
  isVisible = true,
  onToggle
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentView, setCurrentView] = useState('free');
  const [isOrbiting, setIsOrbiting] = useState(false);

  // Theme colors
  const controlsBg = useColorModeValue('bg.accent', 'bg.accent');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const textColor = useColorModeValue('text.default', 'text.default');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');

  // Auto-orbit state
  const orbitRef = useRef(null);

  // Camera movement functions
  const panCamera = (direction) => {
    if (!camera || !controls) return;
    
    const panDistance = 1;
    const panVector = new THREE.Vector3();
    
    switch (direction) {
      case 'left':
        panVector.set(-panDistance, 0, 0);
        break;
      case 'right':
        panVector.set(panDistance, 0, 0);
        break;
      case 'up':
        panVector.set(0, panDistance, 0);
        break;
      case 'down':
        panVector.set(0, -panDistance, 0);
        break;
    }
    
    // Transform pan vector by camera rotation
    panVector.applyQuaternion(camera.quaternion);
    camera.position.add(panVector);
    controls.target.add(panVector);
    controls.update();
  };

  const rotateAroundTarget = (axis, angle) => {
    if (!camera || !controls) return;
    
    const spherical = new THREE.Spherical();
    const offset = new THREE.Vector3();
    
    offset.copy(camera.position).sub(controls.target);
    spherical.setFromVector3(offset);
    
    if (axis === 'horizontal') {
      spherical.theta += angle;
    } else if (axis === 'vertical') {
      spherical.phi += angle;
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
    }
    
    offset.setFromSpherical(spherical);
    camera.position.copy(controls.target).add(offset);
    controls.update();
  };

  const zoomCamera = (direction) => {
    if (!camera || !controls) return;
    
    const zoomFactor = direction === 'in' ? 0.8 : 1.2;
    const offset = new THREE.Vector3();
    offset.copy(camera.position).sub(controls.target);
    offset.multiplyScalar(zoomFactor);
    camera.position.copy(controls.target).add(offset);
    controls.update();
  };

  // Preset view functions
  const setPresetView = (viewType) => {
    if (!camera || !controls || !model) return;
    
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 2;
    
    controls.target.copy(center);
    
    switch (viewType) {
      case 'front':
        camera.position.set(center.x, center.y, center.z + distance);
        break;
      case 'back':
        camera.position.set(center.x, center.y, center.z - distance);
        break;
      case 'left':
        camera.position.set(center.x - distance, center.y, center.z);
        break;
      case 'right':
        camera.position.set(center.x + distance, center.y, center.z);
        break;
      case 'top':
        camera.position.set(center.x, center.y + distance, center.z);
        break;
      case 'bottom':
        camera.position.set(center.x, center.y - distance, center.z);
        break;
      case 'isometric':
        camera.position.set(
          center.x + distance * 0.7,
          center.y + distance * 0.7,
          center.z + distance * 0.7
        );
        break;
    }
    
    setCurrentView(viewType);
    controls.update();
  };

  const resetCamera = () => {
    if (!camera || !controls || !model) return;
    
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 2;
    
    camera.position.set(
      center.x + distance * 0.5,
      center.y + distance * 0.5,
      center.z + distance * 0.5
    );
    controls.target.copy(center);
    setCurrentView('free');
    controls.update();
  };

  const startAutoOrbit = () => {
    if (isOrbiting) {
      if (orbitRef.current) {
        cancelAnimationFrame(orbitRef.current);
        orbitRef.current = null;
      }
      setIsOrbiting(false);
    } else {
      setIsOrbiting(true);
      
      const orbit = () => {
        if (camera && controls) {
          rotateAroundTarget('horizontal', 0.01);
        }
        orbitRef.current = requestAnimationFrame(orbit);
      };
      
      orbit();
    }
  };

  // Focus on specific point
  const focusOnPoint = (point) => {
    if (!camera || !controls) return;
    
    const direction = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
    const distance = camera.position.distanceTo(controls.target);
    
    controls.target.copy(point);
    camera.position.copy(point).add(direction.multiplyScalar(distance));
    controls.update();
  };

  if (!isVisible) return null;

  return (
    <Box
      position="absolute"
      top={2}
      right={80}
      bg={controlsBg}
      borderRadius="md"
      p={2}
      border="1px"
      borderColor={borderColor}
      minW="200px"
      maxW="300px"
    >
      <VStack spacing={3} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Text fontWeight="bold" fontSize="sm" color={textColor}>
            Camera Controls
          </Text>
          <IconButton
            icon={onToggle ? <X /> : <ArrowsOutCardinal />}
            size="xs"
            variant="ghost"
            onClick={onToggle}
          />
        </Flex>

        {/* Pan Controls */}
        <VStack spacing={2} align="stretch">
          <Text fontSize="xs" fontWeight="semibold" color={textColor}>
            Pan Camera
          </Text>
          <VStack spacing={1}>
            <IconButton
              icon={<ArrowUp />}
              size="sm"
              onClick={() => panCamera('up')}
              aria-label="Pan up"
            />
            <HStack spacing={1}>
              <IconButton
                icon={<ArrowLeft />}
                size="sm"
                onClick={() => panCamera('left')}
                aria-label="Pan left"
              />
              <IconButton
                icon={<House />}
                size="sm"
                onClick={resetCamera}
                aria-label="Reset camera"
                variant="outline"
              />
              <IconButton
                icon={<ArrowRight />}
                size="sm"
                onClick={() => panCamera('right')}
                aria-label="Pan right"
              />
            </HStack>
            <IconButton
              icon={<ArrowDown />}
              size="sm"
              onClick={() => panCamera('down')}
              aria-label="Pan down"
            />
          </VStack>
        </VStack>

        <Divider />

        {/* Rotate Controls */}
        <VStack spacing={2} align="stretch">
          <Text fontSize="xs" fontWeight="semibold" color={textColor}>
            Rotate View
          </Text>
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<ArrowCounterClockwise />}
              size="sm"
              onClick={() => rotateAroundTarget('horizontal', -0.2)}
              aria-label="Rotate left"
            />
            <IconButton
              icon={<CaretUp />}
              size="sm"
              onClick={() => rotateAroundTarget('vertical', -0.2)}
              aria-label="Rotate up"
            />
            <IconButton
              icon={<CaretDown />}
              size="sm"
              onClick={() => rotateAroundTarget('vertical', 0.2)}
              aria-label="Rotate down"
            />
            <IconButton
              icon={<ArrowClockwise />}
              size="sm"
              onClick={() => rotateAroundTarget('horizontal', 0.2)}
              aria-label="Rotate right"
            />
          </HStack>
        </VStack>

        <Divider />

        {/* Zoom Controls */}
        <VStack spacing={2} align="stretch">
          <Text fontSize="xs" fontWeight="semibold" color={textColor}>
            Zoom
          </Text>
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<CornersIn />}
              size="sm"
              onClick={() => zoomCamera('in')}
              aria-label="Zoom in"
            />
            <IconButton
              icon={<CornersOut />}
              size="sm"
              onClick={() => zoomCamera('out')}
              aria-label="Zoom out"
            />
          </HStack>
        </VStack>

        <Divider />

        {/* Auto Orbit */}
        <VStack spacing={2} align="stretch">
          <Text fontSize="xs" fontWeight="semibold" color={textColor}>
            Auto Orbit
          </Text>
          <Button
            leftIcon={<CircleNotch />}
            size="sm"
            variant={isOrbiting ? 'solid' : 'outline'}
            colorScheme={isOrbiting ? 'blue' : 'gray'}
            onClick={startAutoOrbit}
          >
            {isOrbiting ? 'Stop' : 'Start'} Orbit
          </Button>
        </VStack>

        <Divider />

        {/* Current View Badge */}
        {currentView !== 'free' && (
          <Badge variant="subtle" textAlign="center" fontSize="xs">
            {currentView.charAt(0).toUpperCase() + currentView.slice(1)} View
          </Badge>
        )}

        {/* Preset Views Toggle */}
        <Button
          size="xs"
          variant="ghost"
          onClick={() => setShowAdvanced(!showAdvanced)}
          rightIcon={showAdvanced ? <X /> : <Eye />}
        >
          Preset Views
        </Button>

        <Collapse in={showAdvanced} animateOpacity>
          <VStack spacing={2} align="stretch" pt={2}>
            <Text fontSize="xs" fontWeight="semibold" color={textColor}>
              Standard Views
            </Text>
            <HStack spacing={1}>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('front')}
                flex={1}
              >
                Front
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('back')}
                flex={1}
              >
                Back
              </Button>
            </HStack>
            <HStack spacing={1}>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('left')}
                flex={1}
              >
                Left
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('right')}
                flex={1}
              >
                Right
              </Button>
            </HStack>
            <HStack spacing={1}>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('top')}
                flex={1}
              >
                Top
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setPresetView('bottom')}
                flex={1}
              >
                Bottom
              </Button>
            </HStack>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setPresetView('isometric')}
            >
              Isometric
            </Button>
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
};

export default ThreeDCameraControls;
